import { useState, useRef, useCallback, useEffect } from "react";
import type { TranslateFn } from "../../i18n";
import type { Profile, ViewState } from "../types";
import { ArrowLeftIcon, ArrowRightIcon, UploadIcon } from "../icons";
import { useIsMobile } from "../utils/useIsMobile";
import { getUnitLabel } from "../utils/units";
import { compressImage, checkAgeBeforeUpload } from "../../image-utils";
import { PhotoStepMobile } from "./PhotoStepMobile";
import { MobileBottomTabs } from "../components/MobileBottomTabs";
import { normalizeProfilePhotoSource, profilePhotoToBlob, profilePhotoToDataUrl } from "../utils/profile-image";

export interface AccessoryField {
  key: string;
  label: string;
  placeholder: { cm: string; in: string };
  hint?: string;
  min?: number;
  max?: number;
  step?: number;
  /** If provided, this image replaces the product image when the field is focused */
  guideImage?: string;
}

interface Props {
  title: string;
  fields: AccessoryField[];
  /** Unit options: first = metric-ish (cm/mm), second = imperial (in) */
  unitOptions?: [{ label: string; value: string }, { label: string; value: string }];
  /** "close-up" swaps the photo step copy to face/head photo guidance */
  photoVariant?: "full-body" | "close-up";
  /** When true, hides the photo upload CTA entirely — manual entry only */
  disablePhotoUpload?: boolean;
  productImage: string;
  productTitle: string;
  formRef: React.MutableRefObject<Record<string, string>>;
  activeProfile?: Profile | null;
  sizingUnit: string;
  setSizingUnit: (u: string) => void;
  setSizingMethod: (m: "exact" | "quick" | null) => void;
  setSizingLoading: (v: boolean) => void;
  setView: (v: ViewState) => void;
  submitSizing: (mode: "exact" | "quick") => void;
  onSnapSubmit: (data: {
    photoFile: File | null;
    photoBase64: string;
    height: number;
    weight: number;
    heightUnit: string;
    weightUnit: string;
    gender: string;
    age?: number;
    extraMeasurements?: Record<string, string | number>;
  }) => void;
  onBack: () => void;
  /** Backend URL + API key — required for the pre-upload age check.
   *  Without these the age-check is skipped (fail-open). */
  apiUrl?: string;
  apiKey?: string;
  onStartFresh?: () => void;
  t: TranslateFn;
}

type LocalStep = "manual" | "photo" | "photo-details";

function CameraUploadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

export function AccessorySizeView({
  title, fields,
  unitOptions,
  photoVariant = "full-body",
  disablePhotoUpload = false,
  productImage, productTitle, formRef, activeProfile,
  sizingUnit, setSizingUnit,
  setSizingMethod, setSizingLoading, setView, submitSizing,
  onSnapSubmit, onBack, apiUrl, apiKey, onStartFresh, t,
}: Props) {
  const isCloseUp = photoVariant === "close-up";
  const isMobile = useIsMobile();
  const [step, setStep] = useState<LocalStep>(() => isCloseUp ? "photo" : "manual");

  // Default unit-toggle labels are localized at render time when the
  // caller didn't ship its own (eyewear ships mm/cm; everything else
  // uses cm/inches).
  const resolvedUnitOptions = unitOptions ?? [
    { label: t("Centimeters"), value: "cm" },
    { label: t("Inches"),      value: "in" },
  ];
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    fields.forEach(f => { init[f.key] = formRef.current[f.key] || ""; });
    return init;
  });
  const [error, setError] = useState("");

  const initialBodyHeightUnit = formRef.current.heightUnit === "in" || formRef.current.heightUnit === "ft" ? "in" : "cm";
  const initialBodyWeightUnit = formRef.current.weightUnit === "lbs" ? "lbs" : "kg";
  const initialHeightValue = parseFloat(formRef.current.height || "");
  const initialTotalInches = initialBodyHeightUnit === "in" && Number.isFinite(initialHeightValue) ? Math.round(initialHeightValue) : null;
  const [bodyHeightUnit, setBodyHeightUnit] = useState<"cm" | "in">(initialBodyHeightUnit);
  const [bodyWeightUnit, setBodyWeightUnit] = useState<"kg" | "lbs">(initialBodyWeightUnit);
  const [bodyHeightCm, setBodyHeightCm] = useState(() => initialBodyHeightUnit === "cm" ? formRef.current.height || "" : "");
  const [bodyHeightFeet, setBodyHeightFeet] = useState(() => initialTotalInches != null ? String(Math.floor(initialTotalInches / 12)) : "");
  const [bodyHeightInches, setBodyHeightInches] = useState(() => initialTotalInches != null ? String(initialTotalInches % 12) : "");
  const [bodyWeight, setBodyWeight] = useState(() => formRef.current.weight || "");
  const seededBodyProfileSignatureRef = useRef<string | null>(null);
  const activeProfilePhoto = activeProfile?.photoUrl || activeProfile?.photoBase64 || null;

  useEffect(() => {
    if (!activeProfile) {
      seededBodyProfileSignatureRef.current = null;
      return;
    }
    const profileHeight = activeProfile.height ?? activeProfile.heightCm;
    const profileWeight = activeProfile.weight ?? activeProfile.weightKg;
    const nextHeightUnit = activeProfile.heightUnit === "in" || activeProfile.heightUnit === "ft" ? "in" : "cm";
    const nextWeightUnit = activeProfile.weightUnit === "lbs" ? "lbs" : "kg";
    const signature = `${activeProfile.id}|${profileHeight ?? ""}|${profileWeight ?? ""}|${nextHeightUnit}|${nextWeightUnit}`;
    if (seededBodyProfileSignatureRef.current === signature) return;
    seededBodyProfileSignatureRef.current = signature;

    if (typeof profileHeight === "number" && Number.isFinite(profileHeight) && profileHeight > 0) {
      formRef.current.height = String(profileHeight);
      formRef.current.heightUnit = nextHeightUnit;
      setBodyHeightUnit(nextHeightUnit);
      if (nextHeightUnit === "in") {
        const totalInches = Math.round(profileHeight);
        setBodyHeightFeet(String(Math.floor(totalInches / 12)));
        setBodyHeightInches(String(totalInches % 12));
        setBodyHeightCm("");
      } else {
        setBodyHeightCm(String(profileHeight));
        setBodyHeightFeet("");
        setBodyHeightInches("");
      }
    }

    if (typeof profileWeight === "number" && Number.isFinite(profileWeight) && profileWeight > 0) {
      formRef.current.weight = String(profileWeight);
      formRef.current.weightUnit = nextWeightUnit;
      setBodyWeightUnit(nextWeightUnit);
      setBodyWeight(String(profileWeight));
    }
    setError("");
  }, [activeProfile, formRef]);

  // Photo state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [uploadHover, setUploadHover] = useState(false);
  // Age-check overlay state — same pattern as BodyProfileView.
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [photoStatus, setPhotoStatus] = useState<string>("");
  const [photoRejection, setPhotoRejection] = useState<string | null>(null);
  const seededPhotoProfileIdRef = useRef<string | null>(null);

  // Age-gate: null = not answered, true = 18+, false = under 18 (blocked).
  // Mirrors the gate BodyProfileView uses — eyewear/headwear are adult-only
  // for the same legal + safety reasons as body try-on.
  const [ageConfirmed, setAgeConfirmed] = useState<boolean | null>(null);

  // Do/Don't/Tip popover — same UX as BodyProfileView's ? button so this
  // screen matches the apparel flow visually and behaviourally.
  const [photoHelpOpen, setPhotoHelpOpen] = useState(false);

  const switchPhotoContextToMetric = () => {
    if (bodyHeightUnit === "in") {
      const totalIn = (parseFloat(bodyHeightFeet) || 0) * 12 + (parseFloat(bodyHeightInches) || 0);
      if (totalIn > 0) setBodyHeightCm(String(Math.round(totalIn * 2.54)));
    }
    if (bodyWeightUnit === "lbs") {
      const lbs = parseFloat(bodyWeight);
      if (lbs > 0) setBodyWeight(String(Math.round(lbs / 2.205)));
    }
    setBodyHeightUnit("cm");
    setBodyWeightUnit("kg");
  };

  const switchPhotoContextToImperial = () => {
    if (bodyHeightUnit === "cm") {
      const cm = parseFloat(bodyHeightCm);
      if (cm > 0) {
        const totalIn = Math.round(cm / 2.54);
        setBodyHeightFeet(String(Math.floor(totalIn / 12)));
        setBodyHeightInches(String(totalIn % 12));
      }
    }
    if (bodyWeightUnit === "kg") {
      const kg = parseFloat(bodyWeight);
      if (kg > 0) setBodyWeight(String(Math.round(kg * 2.205)));
    }
    setBodyHeightUnit("in");
    setBodyWeightUnit("lbs");
  };

  const getPhotoBodyContext = () => {
    const height = bodyHeightUnit === "in"
      ? (parseFloat(bodyHeightFeet) || 0) * 12 + (parseFloat(bodyHeightInches) || 0)
      : parseFloat(bodyHeightCm) || 0;
    const weight = parseFloat(bodyWeight) || 0;
    return { height, weight, heightUnit: bodyHeightUnit, weightUnit: bodyWeightUnit };
  };

  const handleStartFresh = () => {
    if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    formRef.current = {};
    setBodyHeightUnit("cm");
    setBodyWeightUnit("kg");
    setBodyHeightCm("");
    setBodyHeightFeet("");
    setBodyHeightInches("");
    setBodyWeight("");
    setValues(prev => Object.fromEntries(Object.keys(prev).map(key => [key, ""])));
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoBase64(null);
    setAgeConfirmed(null);
    setPhotoRejection(null);
    seededBodyProfileSignatureRef.current = null;
    seededPhotoProfileIdRef.current = null;
    setError("");
    onStartFresh?.();
  };

  const renderActiveProfileCard = (mobile = false) => {
    if (!activeProfile) return null;
    return (
      <div className={`ps-bp-profile-card${mobile ? " ps-bp-profile-card-compact" : ""}`}>
        <div className="ps-bp-profile-card-copy">
          <span className="ps-bp-profile-card-eyebrow">{t("Active Profile")}</span>
          <strong>{activeProfile.name}</strong>
          <span>{t("Using saved measurements for this fit.")}</span>
        </div>
        {onStartFresh && (
          <button type="button" className="ps-bp-profile-card-action" onClick={handleStartFresh}>
            {t("Start Fresh")}
          </button>
        )}
      </div>
    );
  };

  const validatePhotoBodyContext = () => {
    if (!isCloseUp) return true;
    const { height, weight } = getPhotoBodyContext();
    if (bodyHeightUnit === "in") {
      if (!height || height < 36 || height > 96) { setError(t("Please enter a valid height")); return false; }
    } else if (!height || height < 100 || height > 250) {
      setError(t("Please enter a valid height (100-250 cm)"));
      return false;
    }
    if (bodyWeightUnit === "lbs") {
      if (!weight || weight < 66 || weight > 660) { setError(t("Please enter a valid weight")); return false; }
    } else if (!weight || weight < 30 || weight > 300) {
      setError(t("Please enter a valid weight (30-300 kg)"));
      return false;
    }
    setError("");
    return true;
  };

  const renderPhotoBodyContextToggle = (style?: React.CSSProperties) => (
    <div className="ps-bp-system-toggle" style={style}>
      <button className={`ps-bp-system-btn${bodyHeightUnit === "cm" ? " ps-bp-system-active" : ""}`} onClick={switchPhotoContextToMetric} type="button">{t("Metric")}</button>
      <button className={`ps-bp-system-btn${bodyHeightUnit === "in" ? " ps-bp-system-active" : ""}`} onClick={switchPhotoContextToImperial} type="button">{t("Imperial")}</button>
    </div>
  );

  const renderPhotoBodyContextInputs = () => (
    <div className="ps-bp-inline-fields">
      <div className="ps-bp-inline-row">
        <span className="ps-bp-inline-label">{t("HEIGHT")}</span>
        {bodyHeightUnit === "in" ? (
          <div className="ps-bp-inline-input-group">
            <input type="number" className="ps-bp-inline-input" placeholder="5" value={bodyHeightFeet} onChange={e => { setBodyHeightFeet(e.target.value); setError(""); }} min={3} max={8} />
            <span className="ps-bp-inline-unit">ft</span>
            <input type="number" className="ps-bp-inline-input" placeholder="10" value={bodyHeightInches} onChange={e => { setBodyHeightInches(e.target.value); setError(""); }} min={0} max={11} />
            <span className="ps-bp-inline-unit">in</span>
          </div>
        ) : (
          <div className="ps-bp-inline-input-group">
            <input type="number" className="ps-bp-inline-input" placeholder="173" value={bodyHeightCm} onChange={e => { setBodyHeightCm(e.target.value); setError(""); }} min={100} max={250} />
            <span className="ps-bp-inline-unit">cm</span>
          </div>
        )}
      </div>
      <div className="ps-bp-inline-row">
        <span className="ps-bp-inline-label">{t("WEIGHT")}</span>
        <div className="ps-bp-inline-input-group">
          <input type="number" className="ps-bp-inline-input" placeholder={bodyWeightUnit === "kg" ? "75" : "165"} value={bodyWeight} onChange={e => { setBodyWeight(e.target.value); setError(""); }} min={bodyWeightUnit === "kg" ? 30 : 66} max={bodyWeightUnit === "kg" ? 300 : 660} />
          <span className="ps-bp-inline-unit">{bodyWeightUnit}</span>
        </div>
      </div>
    </div>
  );

  const renderPhotoBodyContextFields = (mobile = false, showHeading = true) => {
    if (!isCloseUp) return null;
    return (
      <div className="ps-acc-photo-context" style={{
        padding: mobile ? "0 4%" : 0,
        display: "flex",
        flexDirection: "column",
        gap: mobile ? 10 : "0.65vw",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: mobile ? 10 : "0.6vw",
        }}>
          {showHeading && (
            <span style={{ fontSize: mobile ? 13 : "0.78vw", fontWeight: 700, color: "var(--ps-text-primary)" }}>
              {t("Body details")}
            </span>
          )}
          {renderPhotoBodyContextToggle({ margin: 0 })}
        </div>
        {renderActiveProfileCard(mobile)}
        {renderPhotoBodyContextInputs()}
      </div>
    );
  };

  const handlePhotoSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setPhotoFile(file);
    setPhotoPreview(preview);
    setPhotoProcessing(true);
    setPhotoStatus("Analyzing photo…");
    try {
      // Run age-check (tight 384px thumb) and main compression in parallel
      // so user-perceived latency is bounded by whichever finishes last.
      const ageCheckPromise: Promise<{ isAdult: boolean; confidence: "high" | "low"; reasoning?: string }> =
        apiUrl && apiKey
          ? checkAgeBeforeUpload(file, apiUrl, apiKey)
          : Promise.resolve({ isAdult: true, confidence: "low" });
      // Close-up variants (eyewear / hats) need a sharper, larger crop —
      // the try-on model renders the product directly on the face, so a
      // 512px/65% JPEG smears the eye/ear details Gemini needs to draw
      // against. 1536px / 85% keeps the face crisp without blowing up
      // payload size unreasonably.
      const compressPromise = compressImage(file, isCloseUp ? { maxDimension: 1536, quality: 0.85 } : {});

      const ageResult = await ageCheckPromise;
      if (!ageResult.isAdult) {
        const reason = (ageResult as any).reasoning?.trim() || t("This photo appears to be of a minor. Please upload a photo of someone 18 or older.");
        setPhotoRejection(reason);
        setPhotoFile(null);
        setPhotoPreview(null);
        return;
      }

      const base64 = await compressPromise;
      setPhotoBase64(base64);
      setPhotoRejection(null);
    } catch { /* keep null */ }
    finally {
      setPhotoProcessing(false);
      setPhotoStatus("");
    }
  }, [isCloseUp, apiUrl, apiKey, t]);

  const getCurrentPhotoSource = useCallback(() => {
    return photoBase64 || normalizeProfilePhotoSource(activeProfilePhoto);
  }, [activeProfilePhoto, photoBase64]);

  const getCurrentPhotoFile = useCallback(async (source: string): Promise<File | null> => {
    if (photoFile) return photoFile;
    try {
      const blob = await profilePhotoToBlob(source);
      const file = new File([blob], "profile-photo.jpg", { type: blob.type || "image/jpeg" });
      setPhotoFile(file);
      return file;
    } catch {
      return null;
    }
  }, [photoFile]);

  useEffect(() => {
    const profileId = activeProfile?.id ?? null;
    if (!activeProfilePhoto || !profileId) return;
    if (seededPhotoProfileIdRef.current === profileId) return;
    seededPhotoProfileIdRef.current = profileId;

    let cancelled = false;
    const source = normalizeProfilePhotoSource(activeProfilePhoto);
    if (source) {
      setPhotoPreview(source);
      if (source.startsWith("data:")) setPhotoBase64(source);
      setAgeConfirmed(true);
    }

    profilePhotoToDataUrl(activeProfilePhoto)
      .then(async (dataUrl) => {
        const blob = await profilePhotoToBlob(dataUrl);
        if (cancelled) return;
        setPhotoBase64(dataUrl);
        setPhotoPreview(dataUrl);
        setPhotoFile(new File([blob], "profile-photo.jpg", { type: blob.type || "image/jpeg" }));
        setPhotoRejection(null);
        setError("");
      })
      .catch(() => {
        if (!cancelled && source) {
          setPhotoPreview(source);
          if (source.startsWith("data:")) setPhotoBase64(source);
          setPhotoFile(null);
        }
      });

    return () => { cancelled = true; };
  }, [activeProfile?.id, activeProfilePhoto]);

  const handleRemovePhoto = useCallback(() => {
    if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoBase64(null);
    if (activeProfile?.id) seededPhotoProfileIdRef.current = activeProfile.id;
  }, [activeProfile?.id, photoPreview]);

  const updateValue = (key: string, val: string) => {
    setValues(prev => ({ ...prev, [key]: val }));
    setError("");
  };

  const handleManualSubmit = () => {
    const first = fields[0];
    if (!first) return;
    const firstVal = parseFloat(values[first.key] || "");
    if (!firstVal || firstVal <= 0) {
      setError(t("Please enter your ") + t(first.label).toLowerCase());
      return;
    }
    fields.forEach(f => {
      const v = parseFloat(values[f.key] || "");
      if (v > 0) formRef.current[f.key] = String(v);
    });
    formRef.current.sizingUnit = sizingUnit;
    formRef.current.gender = formRef.current.gender || "male";
    setSizingMethod("exact");
    setSizingLoading(true);
    setView("size-result");
    submitSizing("exact");
  };

  const handlePhotoSubmit = async () => {
    if (photoProcessing) {
      setError(t("Analyzing photo…"));
      return;
    }
    if (ageConfirmed !== true) {
      setError(t("Please confirm that the person in the photo is 18 or older before uploading."));
      return;
    }
    if (!validatePhotoBodyContext()) return;
    const currentPhotoSource = getCurrentPhotoSource();
    if (!currentPhotoSource) {
      setError(t("Please upload a photo"));
      return;
    }
    const submitPhotoFile = await getCurrentPhotoFile(currentPhotoSource);
    const bodyContext = getPhotoBodyContext();
    formRef.current.height = String(bodyContext.height);
    formRef.current.weight = String(bodyContext.weight);
    formRef.current.heightUnit = bodyContext.heightUnit;
    formRef.current.weightUnit = bodyContext.weightUnit;
    onSnapSubmit({
      photoFile: submitPhotoFile,
      photoBase64: currentPhotoSource,
      height: bodyContext.height,
      weight: bodyContext.weight,
      heightUnit: bodyContext.heightUnit,
      weightUnit: bodyContext.weightUnit,
      gender: formRef.current.gender || "male",
    });
  };

  const handlePhotoContinue = () => {
    if (photoProcessing) {
      setError(t("Analyzing photo…"));
      return;
    }
    if (ageConfirmed !== true) {
      setError(t("Please confirm that the person in the photo is 18 or older before uploading."));
      return;
    }
    if (!getCurrentPhotoSource()) {
      setError(t("Please upload a photo"));
      return;
    }
    setError("");
    if (isCloseUp) {
      setStep("photo-details");
      return;
    }
    handlePhotoSubmit();
  };

  const renderScanProgress = (detailsActive: boolean) => (
    <div className="ps-bp-scan-progress">
      {detailsActive ? (
        <button
          type="button"
          className="ps-bp-scan-progress-back"
          onClick={() => setStep("photo")}
          aria-label={t("Back")}
        >
          ←
        </button>
      ) : (
        <span className="ps-bp-scan-progress-spacer" aria-hidden="true" />
      )}
      <div className="ps-bp-scan-progress-track">
        <div className={`ps-bp-scan-progress-step${detailsActive ? " ps-done" : " ps-active"}`}>
          <span className="ps-bp-scan-progress-dot">1</span>
          <span className="ps-bp-scan-progress-label">{t("Photo")}</span>
        </div>
        <div className={`ps-bp-scan-progress-line${detailsActive ? " ps-active" : ""}`} />
        <div className={`ps-bp-scan-progress-step${detailsActive ? " ps-active" : ""}`}>
          <span className="ps-bp-scan-progress-dot">2</span>
          <span className="ps-bp-scan-progress-label">{t("Details")}</span>
        </div>
      </div>
      <span className="ps-bp-scan-progress-spacer" aria-hidden="true" />
    </div>
  );

  const renderPhotoHelpDrawer = () => {
    const doLines = isCloseUp
      ? [
          t("Face the camera directly at eye level"),
          t("Keep your full face, ears, and head visible"),
          t("Use bright, even lighting and a plain background"),
        ]
      : [
          t("Stand facing the camera with your full body in frame"),
          t("Use natural or even lighting"),
          t("Wear fitted, simple clothing"),
        ];
    const dontLines = isCloseUp
      ? [
          t("Don't cover your ears, eyes, or forehead"),
          t("Don't use heavy shadows or strong backlighting"),
          t("Don't use filters, mirrored photos, or angled selfies"),
        ]
      : [
          t("Don't wear loose, baggy, or layered clothing"),
          t("Don't sit, pose, or bend your body"),
          t("Don't use strong backlighting"),
        ];
    return (
      <>
        <div className="ps-bp-photo-help-backdrop" onClick={() => setPhotoHelpOpen(false)} aria-hidden="true" />
        <div role="dialog" aria-label={t("How to take the best photo")} className="ps-bp-photo-help">
          <div className="ps-bp-photo-help-handle" aria-hidden="true" />
          <div className="ps-bp-photo-help-header">
            <span className="ps-bp-photo-help-title">{t("How to take the best photo")}</span>
            <button type="button" onClick={() => setPhotoHelpOpen(false)} aria-label={t("Close")} className="ps-bp-photo-help-close">×</button>
          </div>
          <div className="ps-bp-photo-help-section ps-bp-photo-help-do">
            <div className="ps-bp-photo-help-section-label">✓ {t("Do")}</div>
            <div className="ps-bp-photo-help-section-body">
              {doLines.map((line) => (
                <span key={line}>{line}<br /></span>
              ))}
            </div>
          </div>
          <div className="ps-bp-photo-help-section ps-bp-photo-help-dont">
            <div className="ps-bp-photo-help-section-label">✗ {t("Don't")}</div>
            <div className="ps-bp-photo-help-section-body">
              {dontLines.map((line) => (
                <span key={line}>{line}<br /></span>
              ))}
            </div>
          </div>
          <div className="ps-bp-photo-help-section ps-bp-photo-help-tip">
            <strong>{t("Quick Tip")}:</strong> {isCloseUp
              ? t("A clean close-up improves landmark detection for eyewear and headwear.")
              : t("The simpler your photo is, the more accurate your virtual try-on results will be.")}
          </div>
        </div>
      </>
    );
  };

  const activeUnitLabel = resolvedUnitOptions.find(o => o.value === sizingUnit)?.label || resolvedUnitOptions[0].label;
  const activeUnitShortLabel = sizingUnit === "mm" || sizingUnit === "cm" || sizingUnit === "in"
    ? sizingUnit
    : activeUnitLabel.toLowerCase();
  const unitOptionLabel = (value: string, label: string) =>
    value === "mm" || value === "cm" || value === "in" ? value : t(label);

  // ── Mobile photo step ──────────────────────────────────────────────────────
  if (isMobile && step === "photo") {
    return (
      <div className="ps-bp-wrapper ps-acc-mobile-photo ps-acc-mobile-scan">
          {renderScanProgress(false)}
          <div className="ps-bp-scan-help-row">
            <button
              type="button"
              onClick={() => setPhotoHelpOpen(v => !v)}
              aria-label={t("How to take a good photo")}
              className={`ps-pm-help-pill ps-bp-scan-help-pill${photoHelpOpen ? " ps-active" : ""}`}
            >
              <span className="ps-pm-help-pill-q" aria-hidden="true">?</span>
              <span>{t("How to take a good photo")}</span>
            </button>
          </div>
          <PhotoStepMobile
            photoPreview={photoPreview}
            handlePhotoSelect={handlePhotoSelect}
            handleRemovePhoto={handleRemovePhoto}
            onAnalyze={handlePhotoContinue}
            onSwitchToManual={() => setStep("manual")}
            error={error}
            photoVariant={photoVariant}
            ageConfirmed={ageConfirmed}
            onAgeConfirmedChange={(v) => { setAgeConfirmed(v); if (v === true) setError(""); }}
            primaryLabel={isCloseUp ? t("Continue") : t("ANALYZE MY SIZE")}
            compactAgeGate
            photoProcessing={photoProcessing}
            photoStatus={photoStatus}
            photoRejection={photoRejection}
            onClearRejection={() => setPhotoRejection(null)}
            t={t}
          />
          {photoHelpOpen && renderPhotoHelpDrawer()}
      </div>
    );
  }

  if (isMobile && step === "photo-details") {
    return (
      <div className="ps-bp-wrapper ps-acc-mobile-details">
        {renderScanProgress(true)}
        <div className="ps-bp-scan-details-center ps-acc-scan-details">
          <h2 className="ps-bp-scan-details-title">{t("Your body details")}</h2>
          <p className="ps-bp-scan-details-sub">{t("These calibrate the AI — all required.")}</p>
          {renderActiveProfileCard(true)}
          {renderPhotoBodyContextToggle({ alignSelf: "center", marginTop: 0 })}
          {renderPhotoBodyContextInputs()}
          {error && <p className="ps-bpm-error">{error}</p>}
        </div>
        <div className="ps-bpm-bottom">
          <button type="button" className="ps-bpm-next-btn" onClick={handlePhotoSubmit}>
            {t("Analyze My Size")}
          </button>
          <MobileBottomTabs
            mode="scan"
            onSwitchToManual={() => setStep("manual")}
            onSwitchToScan={() => setStep("photo")}
            t={t}
          />
        </div>
      </div>
    );
  }

  // ── Mobile manual step ─────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="ps-bp-wrapper ps-acc-mobile-wrap">
        <div className="ps-bpm-root ps-accm-root">
          <div className="ps-bpm-header ps-accm-header">
            <h2 className="ps-bpm-title">{t(title)}</h2>
          </div>
          {activeProfile && (
            <div style={{ padding: "0 4%" }}>
              {renderActiveProfileCard(true)}
            </div>
          )}

          {/* Unit toggle — same style as BasicsStepMobile */}
          <div className="ps-bpm-toggle ps-accm-toggle">
            {resolvedUnitOptions.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`ps-bpm-toggle-btn${sizingUnit === opt.value ? " ps-active" : ""}`}
                onClick={() => setSizingUnit(opt.value)}
              >
                {unitOptionLabel(opt.value, opt.label)}
              </button>
            ))}
          </div>

          <div className="ps-accm-fields">
            {fields.map(f => (
              <div key={f.key} className="ps-accm-row">
                <span className="ps-accm-label">{t(f.label).toUpperCase()}</span>
                <div className="ps-accm-input-group">
                  <input
                    type="number"
                    className="ps-accm-input"
                    value={values[f.key]}
                    onChange={e => updateValue(f.key, e.target.value)}
                    placeholder={(f.placeholder as Record<string, string>)[sizingUnit] || f.placeholder.cm || f.placeholder.in || ""}
                    step={f.step ?? 0.5}
                    min={f.min}
                    max={f.max}
                  />
                  <span className="ps-accm-unit">{activeUnitShortLabel}</span>
                </div>
                {f.hint && <span className="ps-accm-hint">{t(f.hint)}</span>}
              </div>
            ))}
            {error && <p className="ps-acc-error" style={{ paddingTop: 8 }}>{error}</p>}
          </div>

          {/* Spacer pushes the "or upload" option to rest just above the
              bottom action bar — visual rhythm matches BasicsStepMobile. */}
          <div style={{ flex: 1, minHeight: 16 }} />

          {/* Photo link */}
          {!disablePhotoUpload && (
            <>
              <div className="ps-bpm-or"><span>{t("or")}</span></div>
              <button type="button" className="ps-bpm-upload-link" onClick={() => setStep("photo")}>
                <CameraUploadIcon />
                <span>{isCloseUp ? t("Upload front face") : t("Upload a photo for AI sizing")}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </>
          )}

          <div className="ps-bpm-bottom ps-accm-bottom">
            <button type="button" className="ps-bpm-next-btn" onClick={handleManualSubmit}>
              {t("See Your Fit")}{getUnitLabel(sizingUnit) ? ` (${getUnitLabel(sizingUnit)})` : ""} <ArrowRightIcon />
            </button>
            {!disablePhotoUpload && (
              <MobileBottomTabs
                mode="manual"
                onSwitchToManual={() => {}}
                onSwitchToScan={() => setStep("photo")}
                t={t}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Desktop layout — merged upload + manual fields (matches BodyProfileView) ─
  // Photo is optional on the desktop flow: user can fill the manual fields
  // and hit "See Your Fit", OR drop in a photo and hit "Analyze".
  const anyFieldFilled = fields.some(f => (parseFloat(values[f.key] || "") || 0) > 0);
  const canAnalyze = !!getCurrentPhotoSource() && ageConfirmed === true && !photoProcessing;
  const canFind = anyFieldFilled;
  // Block submit while the age check is running — same behaviour as the
  // body / profile-card flows.
  const submitDisabled = photoProcessing || (!canAnalyze && !canFind);
  const submitLabel = photoProcessing
    ? t("Analyzing photo…")
    : canAnalyze
      ? t("Analyze")
      : `${t("See Your Fit")}${getUnitLabel(sizingUnit) ? ` (${getUnitLabel(sizingUnit)})` : ""}`;
  const handleSubmit = canAnalyze ? handlePhotoSubmit : handleManualSubmit;

  // ── First screen — manual-only (no upload zone, no age gate) ───────────
  // Mirrors the "basics" step in BodyProfileView. User lands here, fills
  // measurements, OR clicks the "upload photo" CTA to go to the photo
  // step (which has the upload zone + age gate). Previously these were
  // merged — that caused the age-verification dialog to appear on first
  // load next to a blurred dropzone, which is wrong.
  // Footwear sets `disablePhotoUpload` — there's no close-up photo flow for
  // shoes (the AI has no way to size a shoe from a photo of a body/foot),
  // so we hard-gate the photo step here too. Even if something flipped
  // `step` to "photo" we fall back to the manual layout.
  if (step !== "photo" || disablePhotoUpload) {
    return (
      <div className="ps-bp-layout">
        <div className="ps-bp-image" style={{ position: "relative" }}>
          <img src={productImage} alt={productTitle} className="ps-bp-image-img" />
        </div>
        <div className="ps-bp-root">
          <div className="ps-bp-step ps-bp-step-enter">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6vw", marginBottom: "0.3vw" }}>
              <h2 className="ps-bp-title" style={{ margin: 0 }}>{t(title)}</h2>
            </div>
            {renderActiveProfileCard(false)}
            <div className="ps-bp-system-toggle">
              {resolvedUnitOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`ps-bp-system-btn${sizingUnit === opt.value ? " ps-bp-system-active" : ""}`}
                  onClick={() => setSizingUnit(opt.value)}
                >
                  {t(opt.label)}
                </button>
              ))}
            </div>
            <div className="ps-bp-inline-fields">
              {fields.map(f => (
                <div key={f.key} className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t(f.label).toUpperCase()}</span>
                  <div className="ps-bp-inline-input-group">
                    <input
                      type="number"
                      className="ps-bp-inline-input"
                      value={values[f.key]}
                      onChange={e => updateValue(f.key, e.target.value)}
                      placeholder={(f.placeholder as Record<string, string>)[sizingUnit] || f.placeholder.cm || f.placeholder.in || ""}
                      step={f.step ?? 0.5}
                      min={f.min}
                      max={f.max}
                    />
                    <span className="ps-bp-inline-unit">{activeUnitLabel.toLowerCase()}</span>
                  </div>
                </div>
              ))}
            </div>
            {error && <p className="ps-bp-error">{error}</p>}
            {!disablePhotoUpload && (
              <button className="ps-bp-photo-cta" onClick={() => setStep("photo")} type="button">
                <CameraUploadIcon />
                <span>{isCloseUp ? t("Or upload front face") : t("Or upload a photo for AI sizing")}</span>
                <span className="ps-bp-photo-cta-arrow">&rarr;</span>
              </button>
            )}
            <div className="ps-bp-nav" style={{ marginTop: "auto" }}>
              <div />
              <button className="ps-bp-next-btn" onClick={handleManualSubmit} type="button">
                {t("See Your Fit")}{getUnitLabel(sizingUnit) ? ` (${getUnitLabel(sizingUnit)})` : ""} <ArrowRightIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Photo step — merged upload zone + inputs (age gate gates the drop zone) ─
  return (
    <div className="ps-bp-layout">
      {/* Photo step: no left product tile; upload zone replaces it. */}
      <div className="ps-bp-root" style={{ width: "100%" }}>
        {/* ── Merged upload + inputs (same layout as BodyProfileView photo step) ── */}
        <div className="ps-bp-wrapper" style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
            <div style={{ display: "flex", gap: "1.2vw", padding: "1.5vw", width: "100%", flex: 1, minHeight: "20vw", alignItems: "stretch" }}>
              {/* Left: Upload area — identical pattern to BodyProfileView's
                  photo step. Hover accent border, no blur, age chip is
                  INLINE inside the dropzone (below the JPEG hint) rather
                  than a modal overlay, so the shopper sees the question
                  without the zone being washed out. Portrait aspect-lock
                  on close-up variants keeps the face preview from
                  stretching as the viewport changes. */}
              <div style={{
                flex: 1, position: "relative", display: "flex",
                ...(isCloseUp ? { aspectRatio: "3 / 4", alignSelf: "center", maxHeight: "100%" } : null),
              }}>
                <div
                  onClick={() => { if (!photoPreview && ageConfirmed === true) fileInputRef.current?.click(); }}
                  onMouseEnter={() => { if (!photoPreview && ageConfirmed === true) setUploadHover(true); }}
                  onMouseLeave={() => setUploadHover(false)}
                  style={{
                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    border: photoPreview
                      ? "2px solid var(--ps-accent)"
                      : (ageConfirmed === true && uploadHover)
                        ? "2px dashed var(--ps-accent)"
                        : "2px dashed var(--ps-border-color)",
                    borderRadius: "0.5vw",
                    cursor: photoPreview ? "default" : (ageConfirmed === true ? "pointer" : "default"),
                    position: "relative",
                    background: photoPreview
                      ? "var(--ps-bg-secondary)"
                      : (ageConfirmed === true && uploadHover)
                        ? "rgba(33,84,239,0.04)"
                        : ageConfirmed === null
                          ? "rgba(0,0,0,0.025)"
                          : "var(--ps-bg-secondary)",
                    transition: "border-color 0.18s, background 0.18s", overflow: "hidden",
                  }}
                >
                  {/* Analyzing overlay — covers the upload zone while we run
                      the age check + compress, mirroring BodyProfileView. */}
                  {photoProcessing && (
                    <div className="ps-bp-photo-overlay" onClick={e => e.stopPropagation()}>
                      <div className="ps-bp-photo-overlay-spinner" aria-hidden="true" />
                      <span className="ps-bp-photo-overlay-label">{photoStatus || t("Analyzing photo…")}</span>
                    </div>
                  )}
                  {photoPreview ? (
                    <>
                      <img src={photoPreview} alt={t("preview")} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      <button onClick={e => { e.stopPropagation(); handleRemovePhoto(); }} style={{ position: "absolute", top: "0.5vw", right: "0.5vw", width: "1.4vw", height: "1.4vw", borderRadius: "50%", background: "rgba(0,0,0,0.6)", border: "none", color: "#fff", fontSize: "0.7vw", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>&times;</button>
                    </>
                  ) : (
                    <>
                      <UploadIcon size={32} />
                      <span style={{ fontSize: "0.85vw", fontWeight: 600, color: "var(--ps-text-primary)", marginTop: "0.5vw" }}>
                        {isCloseUp ? t("Upload front face") : t("Upload your photo")}
                      </span>
                      <span style={{ fontSize: "0.6vw", color: "var(--ps-text-muted)", marginTop: "0.2vw" }}>
                        {isCloseUp ? t("Click or drag a front face photo") : t("Click or drag a full-body photo")}
                      </span>
                      <span style={{ fontSize: "0.5vw", color: "var(--ps-text-dim)", marginTop: "0.4vw" }}>{t("JPEG, PNG (max 10MB)")}</span>

                      {/* Photo-rejection card — shown when age-check failed.
                          Sits in the same slot as the "Are you 18 or older?"
                          chip so the rejection feedback feels native. */}
                      {photoRejection && (
                        <div
                          role="alert"
                          onClick={e => e.stopPropagation()}
                          style={{
                            marginTop: "1.1vw",
                            padding: "0.75vw 1.1vw",
                            background: "#FFFFFF",
                            border: "1px solid rgba(220, 38, 38, 0.35)",
                            borderRadius: "0.55vw",
                            boxShadow: "0 6px 18px -8px rgba(220, 38, 38, 0.25)",
                            display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.55vw",
                            cursor: "default", maxWidth: "20vw",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "0.45vw" }}>
                            <svg viewBox="0 0 24 24" width="0.85vw" height="0.85vw" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span style={{ color: "#dc2626", fontSize: "0.7vw", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{t("Different photo needed")}</span>
                          </div>
                          <div style={{ fontSize: "0.62vw", color: "var(--ps-text-secondary)", lineHeight: 1.55 }}>
                            {photoRejection}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPhotoRejection(null); fileInputRef.current?.click(); }}
                            style={{
                              padding: "0.35vw 0.8vw",
                              background: "var(--ps-accent)",
                              color: "#fff",
                              border: "none",
                              borderRadius: "0.35vw",
                              fontSize: "0.62vw",
                              fontWeight: 700,
                              cursor: "pointer",
                              fontFamily: "inherit",
                            }}
                          >{t("Try another photo")}</button>
                        </div>
                      )}

                      {/* Inline age-verification chip — sits a breath below
                          the JPEG/PNG hint, not floating in the middle of
                          the zone as a modal overlay. */}
                      {ageConfirmed === null && !photoRejection && (
                        <div
                          role="dialog"
                          onClick={e => e.stopPropagation()}
                          style={{
                            marginTop: "1.1vw",
                            padding: "0.75vw 1.1vw",
                            background: "#FFFFFF",
                            border: "1px solid var(--ps-border-color)",
                            borderRadius: "0.55vw",
                            boxShadow: "0 6px 18px -8px rgba(17,24,39,0.22)",
                            display: "flex", alignItems: "center", gap: "0.8vw",
                            cursor: "default",
                          }}
                        >
                          <span style={{ fontSize: "0.78vw", fontWeight: 600, color: "var(--ps-text-primary)" }}>
                            {t("Are you 18 or older?")}
                          </span>
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); setAgeConfirmed(true); setError(""); }}
                            style={{
                              padding: "0.4vw 1vw", borderRadius: "999px",
                              background: "var(--ps-accent)", color: "#FFFFFF",
                              border: "1px solid var(--ps-accent)",
                              fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            {t("Yes")}
                          </button>
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); setAgeConfirmed(false); }}
                            style={{
                              padding: "0.4vw 1vw", borderRadius: "999px",
                              background: "transparent", color: "var(--ps-text-secondary)",
                              border: "1px solid var(--ps-border-color)",
                              fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            {t("No")}
                          </button>
                        </div>
                      )}

                      {ageConfirmed === false && (
                        <div
                          role="alert"
                          onClick={e => e.stopPropagation()}
                          style={{
                            marginTop: "1.1vw",
                            padding: "0.75vw 1.1vw",
                            background: "#FFFFFF",
                            border: "1px solid rgba(192,38,38,0.4)",
                            borderRadius: "0.55vw",
                            boxShadow: "0 6px 18px -8px rgba(192,38,38,0.22)",
                            display: "flex", alignItems: "center", gap: "0.8vw",
                            cursor: "default",
                          }}
                        >
                          <span style={{ fontSize: "0.78vw", fontWeight: 600, color: "#C02626" }}>
                            {t("Under 18 — can't process this photo.")}
                          </span>
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); setAgeConfirmed(null); }}
                            style={{
                              padding: "0.4vw 1vw", borderRadius: "999px",
                              background: "transparent", color: "var(--ps-text-secondary)",
                              border: "1px solid var(--ps-border-color)",
                              fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700, cursor: "pointer",
                            }}
                          >
                            {t("Go back")}
                          </button>
                        </div>
                      )}

                      {isCloseUp && ageConfirmed === true && (
                        <div
                          onClick={e => e.stopPropagation()}
                          style={{
                            position: "absolute", left: "0.8vw", right: "0.8vw", bottom: "0.6vw",
                            fontSize: "0.62vw", lineHeight: 1.5,
                            color: "var(--ps-text-secondary)",
                            textAlign: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3vw", verticalAlign: "middle" }}>
                            <svg viewBox="0 0 24 24" width="0.8vw" height="0.8vw" fill="none" stroke="#2154ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            <span style={{ fontWeight: 700, color: "#2154ef", letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("Legal Notice")}</span>
                          </span>
                          {" — "}
                          {t("Your image will be used to generate a virtual try-on preview showing how selected items may look and fit. Images are processed securely and are not stored after generation.")}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Soft vertical divider between upload and right-column inputs
                  (mirror of BodyProfileView). Gradient fade top+bottom gives
                  the "hair-thin rule" feel rather than a hard full-height
                  line. */}
              <div aria-hidden="true" style={{
                width: "1px",
                alignSelf: "stretch",
                background: "linear-gradient(to bottom, transparent 0%, var(--ps-border-subtle) 18%, var(--ps-border-subtle) 82%, transparent 100%)",
                flexShrink: 0,
              }} />

              {/* Right column mirrors BodyProfileView: body details stay visible,
                  while Do/Don't/Tip guidance lives behind the ? help popover.
                  Close-up products still ask only height + weight here. */}
              <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: isCloseUp ? "flex-start" : "space-between",
                position: "relative",
                gap: isCloseUp ? "0.8vw" : "0.6vw",
              }}>

                {isCloseUp ? (
                  <>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6vw" }}>
                        <h3 style={{ margin: 0, fontSize: "1vw", fontWeight: 700, color: "var(--ps-text-primary)" }}>{t("Body details")}</h3>
                        <button
                          type="button"
                          onClick={() => setPhotoHelpOpen(v => !v)}
                          aria-label={t("How to take a good photo")}
                          title={t("How to take a good photo")}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: "0.35vw",
                            padding: "0.4vw 0.7vw",
                            background: photoHelpOpen ? "var(--ps-accent)" : "transparent",
                            color: photoHelpOpen ? "#FFFFFF" : "var(--ps-accent)",
                            border: "1.5px solid var(--ps-accent)",
                            borderRadius: "999px",
                            cursor: "pointer", fontFamily: "inherit",
                            fontSize: "0.58vw", fontWeight: 700,
                            letterSpacing: "0.04em", whiteSpace: "nowrap",
                            transition: "background 0.15s, color 0.15s",
                            flexShrink: 0,
                          }}
                        >
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: "0.95vw", height: "0.95vw", minWidth: "14px", minHeight: "14px",
                            borderRadius: "50%",
                            background: photoHelpOpen ? "#FFFFFF" : "var(--ps-accent)",
                            color: photoHelpOpen ? "var(--ps-accent)" : "#FFFFFF",
                            fontSize: "0.65vw", fontWeight: 700, lineHeight: 1,
                          }} aria-hidden="true">?</span>
                          <span>{t("How to take a good photo")}</span>
                        </button>
                      </div>
                      <p style={{ margin: "0.2vw 0 0", fontSize: "0.65vw", color: "var(--ps-text-muted)" }}>
                        {t("These calibrate the AI — all required.")}
                      </p>
                    </div>

                    {renderActiveProfileCard(false)}

                    {renderPhotoBodyContextToggle({ alignSelf: "center", marginTop: "1.2vw" })}

                    <div style={{ marginTop: "auto", marginBottom: "auto" }}>
                      {renderPhotoBodyContextInputs()}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6vw" }}>
                        <h3 style={{ margin: 0, fontSize: "1vw", fontWeight: 700, color: "var(--ps-text-primary)" }}>{t(title)}</h3>
                        <button
                          type="button"
                          onClick={() => setPhotoHelpOpen(v => !v)}
                          aria-label={t("How to take a good photo")}
                          title={t("How to take a good photo")}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: "0.35vw",
                            padding: "0.4vw 0.7vw",
                            background: photoHelpOpen ? "var(--ps-accent)" : "transparent",
                            color: photoHelpOpen ? "#FFFFFF" : "var(--ps-accent)",
                            border: "1.5px solid var(--ps-accent)",
                            borderRadius: "999px",
                            cursor: "pointer", fontFamily: "inherit",
                            fontSize: "0.58vw", fontWeight: 700,
                            letterSpacing: "0.04em", whiteSpace: "nowrap",
                            transition: "background 0.15s, color 0.15s",
                            flexShrink: 0,
                          }}
                        >
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: "0.95vw", height: "0.95vw", minWidth: "14px", minHeight: "14px",
                            borderRadius: "50%",
                            background: photoHelpOpen ? "#FFFFFF" : "var(--ps-accent)",
                            color: photoHelpOpen ? "var(--ps-accent)" : "#FFFFFF",
                            fontSize: "0.65vw", fontWeight: 700, lineHeight: 1,
                          }} aria-hidden="true">?</span>
                          <span>{t("How to take a good photo")}</span>
                        </button>
                      </div>
                      <p style={{ margin: "0.2vw 0 0", fontSize: "0.65vw", color: "var(--ps-text-muted)" }}>
                        {t("Enter your measurements OR upload a photo — we'll use whichever you provide.")}
                      </p>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.7vw" }}>
                      <div className="ps-bp-system-toggle" style={{ alignSelf: "center" }}>
                        {resolvedUnitOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            className={`ps-bp-system-btn${sizingUnit === opt.value ? " ps-bp-system-active" : ""}`}
                            onClick={() => setSizingUnit(opt.value)}
                          >
                            {t(opt.label)}
                          </button>
                        ))}
                      </div>

                      <div className="ps-bp-inline-fields">
                        {fields.map(f => (
                          <div key={f.key} className="ps-bp-inline-row">
                            <span className="ps-bp-inline-label">{t(f.label).toUpperCase()}</span>
                            <div className="ps-bp-inline-input-group">
                              <input
                                type="number"
                                className="ps-bp-inline-input"
                                value={values[f.key]}
                                onChange={e => updateValue(f.key, e.target.value)}
                                placeholder={(f.placeholder as Record<string, string>)[sizingUnit] || f.placeholder.cm || f.placeholder.in || ""}
                                step={f.step ?? 0.5}
                                min={f.min}
                                max={f.max}
                              />
                              <span className="ps-bp-inline-unit">{activeUnitLabel.toLowerCase()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {!isCloseUp && (
                  <div style={{
                    background: "rgba(33, 84, 239, 0.04)",
                    border: "1px solid rgba(33, 84, 239, 0.16)",
                    borderRadius: "0.55vw",
                    padding: "0.6vw 0.85vw",
                    display: "flex", flexDirection: "column", gap: "0.3vw",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35vw" }}>
                      <svg viewBox="0 0 24 24" width="0.75vw" height="0.75vw" fill="none" stroke="#2154ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                      <span style={{ color: "#2154ef", fontSize: "0.62vw", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>{t("Legal Notice")}</span>
                    </div>
                    <div style={{ fontSize: "0.58vw", color: "var(--ps-text-secondary)", lineHeight: 1.55 }}>
                      {t("Your image will be used to generate a virtual try-on preview showing how selected items may look and fit. Images are processed securely and are not stored after generation.")}
                    </div>
                  </div>
                )}

                {/* Photo-tips popover — opens on ? click, absolute-positioned
                    so it doesn't shift the layout. Content is variant-aware
                    (close-up vs full-body) to keep guidance relevant. */}
                {photoHelpOpen && (
                  <div
                    role="dialog"
                    aria-label={t("How to take the best photo")}
                    style={{
                      position: "absolute", top: "2.2vw", right: 0,
                      width: "min(360px, 22vw)",
                      background: "#FFFFFF",
                      border: "1px solid var(--ps-border-subtle)",
                      borderRadius: "0.8vw",
                      boxShadow: "0 20px 40px -12px rgba(17,24,39,0.25), 0 8px 16px -8px rgba(17,24,39,0.15)",
                      padding: "0.8vw 1vw",
                      display: "flex", flexDirection: "column", gap: "0.6vw",
                      zIndex: 5,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "0.8vw", fontWeight: 700, color: "var(--ps-text-primary)" }}>{t("How to take the best photo")}</span>
                      <button
                        type="button"
                        onClick={() => setPhotoHelpOpen(false)}
                        aria-label={t("Close")}
                        style={{ width: "1.4vw", height: "1.4vw", borderRadius: "50%", background: "transparent", border: "none", color: "var(--ps-text-muted)", cursor: "pointer", fontSize: "1vw", lineHeight: 1 }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ background: "#ddfbe7", borderRadius: "0.5vw", padding: "0.55vw 0.75vw" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.3vw", marginBottom: "0.3vw" }}>
                        <span style={{ color: "#1c9d4c", fontSize: "0.7vw", fontWeight: 700 }}>✓ {t("Do")}</span>
                      </div>
                      <div style={{ fontSize: "0.6vw", color: "var(--ps-text-primary)", lineHeight: 1.6 }}>
                        {isCloseUp ? (
                          <>
                            {t("Face the camera directly, centered in frame")}<br />
                            {t("Use natural, even lighting (e.g. near a window)")}<br />
                            {t("Keep hair away from your face and ears")}<br />
                            {t("Choose a plain, light background")}<br />
                            {t("Hold the camera at eye level")}
                          </>
                        ) : (
                          <>
                            {t("Stand facing the camera with your full body in frame")}<br />
                            {t("Use natural or even lighting (e.g. near a window)")}<br />
                            {t("Wear fitted, simple clothing (e.g. neutral colors)")}<br />
                            {t("Choose a plain background (a light wall is ideal)")}<br />
                            {t("Stand straight and still, arms relaxed by your sides")}
                          </>
                        )}
                      </div>
                    </div>

                    <div style={{ background: "#ffe2e2", borderRadius: "0.5vw", padding: "0.55vw 0.75vw" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.3vw", marginBottom: "0.3vw" }}>
                        <span style={{ color: "#e7000b", fontSize: "0.7vw", fontWeight: 700 }}>✗ {t("Don't")}</span>
                      </div>
                      <div style={{ fontSize: "0.6vw", color: "var(--ps-text-primary)", lineHeight: 1.6 }}>
                        {isCloseUp ? (
                          <>
                            {t("Don't wear sunglasses or a hat in the photo")}<br />
                            {t("Don't tilt or turn your head")}<br />
                            {t("Don't use strong backlighting or flash")}<br />
                            {t("Don't crop out your ears or top of head")}<br />
                            {t("Don't apply filters or heavy edits")}
                          </>
                        ) : (
                          <>
                            {t("Don't wear loose, baggy, or layered clothing")}<br />
                            {t("Don't sit, pose, or bend your body")}<br />
                            {t("Don't use strong backlighting")}<br />
                            {t("Don't take mirror photos or selfies")}<br />
                            {t("Don't apply filters, effects, or edits")}
                          </>
                        )}
                      </div>
                    </div>

                    <div style={{ background: "#c6e1f7", borderRadius: "0.5vw", padding: "0.55vw 0.75vw", fontSize: "0.6vw", color: "var(--ps-text-primary)", lineHeight: 1.55 }}>
                      <strong style={{ color: "#3267c3" }}>💡 {t("Quick Tip")}:</strong> {isCloseUp
                        ? t("A clear, well-lit face photo gives the most accurate fit for eyewear and headwear.")
                        : t("The simpler your photo is, the more accurate your virtual try-on results will be.")}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhotoSelect} />
            {error && <p className="ps-bp-error" style={{ padding: "0 1.5vw" }}>{error}</p>}

            <div className="ps-bp-nav">
              <button className="ps-bp-back-btn" onClick={() => setStep("manual")} type="button">
                <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
              </button>
              <button
                className={`ps-bp-next-btn${submitDisabled ? " ps-bp-btn-disabled" : ""}`}
                onClick={handleSubmit}
                disabled={submitDisabled}
                type="button"
              >
                {submitLabel} <ArrowRightIcon />
              </button>
            </div>
        </div>
      </div>
    </div>
  );
}
